/**
 * Identity sheets — sign-in (OTP) + profile ("small on purpose").
 *
 * Rendered once at app level; bottom sheet on phones, right dock on
 * desktop (same pattern as the board sheet). Identity is never the gate:
 * these sheets only ever add persistence to an anonymous session.
 *
 * Honesty rules encoded here:
 *  - the promise copy sits at the exact moment of asking for the key
 *  - the ledger claims only what Desk actually does: the identifier lives
 *    in tables never joined to leads; visits run on the anonymous line
 *  - when the dev worker echoes the OTP (no SMS/email provider wired yet)
 *    the UI says so instead of pretending a message was sent
 */

import { useEffect, useRef, useState } from 'react';
import { useBuyerAuth } from '@/lib/buyer-auth-context';
import { useAdvisor } from '@/lib/advisor-context';
import type { BuyerMethod, StartResponse } from '@/lib/buyer-auth-api';

export function IdentitySheets() {
  const { identitySheet, closeIdentity, buyer } = useBuyerAuth();
  if (identitySheet === 'none') return null;
  return (
    <>
      <div className="ot-scrim ot-scrim-on" onClick={closeIdentity} aria-hidden="true" />
      <div className="ot-sheet ot-sheet-on" role="dialog" aria-modal="true">
        {identitySheet === 'signin' && !buyer && <SignInFlow />}
        {(identitySheet === 'profile' || (identitySheet === 'signin' && buyer)) && <ProfileSheet />}
      </div>
    </>
  );
}

function SheetHead({ title, sub }: { title: string; sub?: string }) {
  const { closeIdentity } = useBuyerAuth();
  return (
    <div className="ot-sheet-hd">
      <div className="ot-grab" />
      <h3>{title}</h3>
      {sub && <div className="ot-sheet-sub">{sub}</div>}
      <button type="button" className="ot-sheet-x" onClick={closeIdentity} aria-label="Close">
        ✕
      </button>
    </div>
  );
}

function SignInFlow() {
  const { startSignIn, verifyCode } = useBuyerAuth();
  const [step, setStep] = useState<'method' | 'code' | 'done'>('method');
  const [method, setMethod] = useState<BuyerMethod>('phone');
  const [identifier, setIdentifier] = useState('');
  const [start, setStart] = useState<StartResponse | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    if (!identifier.trim() || busy) return;
    setBusy(true);
    setError('');
    const res = await startSignIn(method, identifier);
    setBusy(false);
    if (res.status !== 'ok' || !res.challenge_id) {
      setError(
        res.error === 'rate_limited'
          ? `Too many codes — try again in ${Math.ceil((res.retry_after_s ?? 900) / 60)} min.`
          : res.error === 'invalid_phone'
            ? 'That doesn’t look like an Indian mobile number.'
            : res.error === 'invalid_email'
              ? 'That doesn’t look like an email address.'
              : 'Couldn’t reach the sign-in service — try again.',
      );
      return;
    }
    setStart(res);
    setStep('code');
  };

  const onVerified = () => setStep('done');

  if (step === 'method') {
    return (
      <>
        <SheetHead title="Your sign-in key" sub="One code. Nothing else." />
        <div className="ot-sheet-body">
          <div className="au-seg">
            <button
              type="button"
              className={method === 'phone' ? 'on' : ''}
              onClick={() => setMethod('phone')}
            >
              Phone number
            </button>
            <button
              type="button"
              className={method === 'email' ? 'on' : ''}
              onClick={() => setMethod('email')}
            >
              Email
            </button>
          </div>
          <div className="au-field">
            <label htmlFor="au-id">{method === 'phone' ? 'Mobile number' : 'Email address'}</label>
            <input
              id="au-id"
              type={method === 'phone' ? 'tel' : 'email'}
              inputMode={method === 'phone' ? 'tel' : 'email'}
              placeholder={method === 'phone' ? '+91 · used once, for the code' : 'you@example.com'}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void sendCode();
              }}
            />
          </div>
          <div className="au-promise">
            <b>Authentication only.</b> We use this to send one sign-in code — never to call you,
            never for marketing, never shared with builders. If you ever book a visit, it&apos;s you
            who chooses the one site team that gets your number.
          </div>
          {error && <div className="au-error">{error}</div>}
          <button type="button" className="ot-btn-dark" disabled={busy} onClick={() => void sendCode()}>
            {busy ? 'Sending…' : 'Send my code'}
          </button>
          <p className="au-fine">
            By continuing you agree that Naya stores this identifier to operate your profile. Delete
            it any time from your profile — everything linked to it is erased.
          </p>
        </div>
      </>
    );
  }

  if (step === 'code' && start) {
    return <CodeStep start={start} onVerified={onVerified} verify={verifyCode} />;
  }

  return <SignedInStep />;
}

function CodeStep({
  start,
  onVerified,
  verify,
}: {
  start: StartResponse;
  onVerified: () => void;
  verify: (challengeId: string, code: string) => Promise<{ ok: boolean; error?: string }>;
}) {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const submit = async (code: string) => {
    if (busy) return;
    setBusy(true);
    setError('');
    const res = await verify(start.challenge_id!, code);
    setBusy(false);
    if (!res.ok) {
      setDigits(['', '', '', '', '', '']);
      refs.current[0]?.focus();
      setError(
        res.error === 'wrong_code'
          ? 'That code didn’t match — check and try again.'
          : res.error === 'expired'
            ? 'That code expired — close this and request a fresh one.'
            : res.error === 'too_many_attempts'
              ? 'Too many tries — request a fresh code.'
              : 'Couldn’t verify — try again.',
      );
      return;
    }
    onVerified();
  };

  const setDigit = (i: number, v: string) => {
    const clean = v.replace(/\D/g, '');
    setDigits((d) => {
      const next = [...d];
      if (clean.length > 1) {
        // Paste path — spread across boxes.
        for (let j = 0; j < clean.length && i + j < 6; j++) next[i + j] = clean[j]!;
      } else {
        next[i] = clean;
      }
      const full = next.join('');
      if (full.length === 6) void submit(full);
      else if (clean) refs.current[Math.min(i + clean.length, 5)]?.focus();
      return next;
    });
  };

  return (
    <>
      <SheetHead
        title="Enter your code"
        sub={`Sent to ${start.masked ?? 'your key'} — nothing else will ever be sent there.`}
      />
      <div className="ot-sheet-body">
        <div className="au-otp">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => {
                refs.current[i] = el;
              }}
              value={d}
              maxLength={6}
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
              }}
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>
        {start.dev_code && (
          <button
            type="button"
            className="au-devfill"
            onClick={() => {
              setDigits(start.dev_code!.split(''));
              void submit(start.dev_code!);
            }}
          >
            Dev build — no SMS/email provider wired yet. Tap to use code {start.dev_code}.
          </button>
        )}
        <div className="au-promise">
          <b>Why a code?</b> It proves this key is yours, so your board follows you to any device —
          and no one can pretend to be you on visit day.
        </div>
        {error && <div className="au-error">{error}</div>}
        {busy && <div className="au-fine">Checking…</div>}
        <p className="au-fine">
          We never send anything to this {start.masked?.includes('@') ? 'address' : 'number'} except
          sign-in codes.
        </p>
      </div>
    </>
  );
}

function SignedInStep() {
  const { buyer, closeIdentity, openProfile } = useBuyerAuth();
  return (
    <>
      <SheetHead title="You're in" sub="Profile created — your board now follows you." />
      <div className="ot-sheet-body">
        <div className="au-row">
          <span>
            <span className="au-k">Signed in as</span>
            <span className="au-v">{buyer?.masked ?? '—'}</span>
          </span>
          <span className="au-ok">✓ verified</span>
        </div>
        <div className="au-promise">
          <b>What we do with this:</b> sign you in. <b>What we never do:</b> call, message (beyond
          codes), sell, or share it — builders included.
        </div>
        <button type="button" className="ot-btn-dark" onClick={openProfile}>
          See your profile
        </button>
        <button type="button" className="ot-btn-line" onClick={closeIdentity}>
          Back to Naya
        </button>
      </div>
    </>
  );
}

function ProfileSheet() {
  const { buyer, signOut, deleteProfile, closeIdentity, lastSyncedAt } = useBuyerAuth();
  const { bookedVisits } = useAdvisor();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [busy, setBusy] = useState(false);

  if (deleted) {
    return (
      <>
        <SheetHead title="Profile deleted" />
        <div className="ot-sheet-body">
          <div className="au-promise">
            <b>Gone.</b> Key, sessions and your synced board erased from Naya — immediately,
            permanently. You can keep using Naya anonymously — that was always allowed.
          </div>
          <button
            type="button"
            className="ot-btn-line"
            onClick={() => {
              closeIdentity();
              window.location.reload();
            }}
          >
            Close
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <SheetHead title="Your profile" sub="Small on purpose." />
      <div className="ot-sheet-body">
        <div className="au-row">
          <span>
            <span className="au-k">Sign-in key</span>
            <span className="au-v">{buyer?.masked ?? '—'}</span>
          </span>
          <span className="au-ok">✓ verified</span>
        </div>
        <div className="au-row">
          <span>
            <span className="au-k">Used for</span>
            <span className="au-v">Signing you in — nothing else</span>
          </span>
        </div>
        <div className="au-row">
          <span>
            <span className="au-k">Your brief &amp; board</span>
            <span className="au-v">
              {lastSyncedAt ? 'Synced · on any device' : 'Syncs as you chat'}
            </span>
          </span>
        </div>
        <div className="au-ledger">
          <h4>Who has your number</h4>
          <div className="au-ledger-row">
            <span>Builders</span>
            <span className="au-none">Nobody</span>
          </div>
          <div className="au-ledger-row">
            <span>Marketing lists</span>
            <span className="au-none">Nobody</span>
          </div>
          <p className="au-fine au-fine-tight">
            {bookedVisits.length > 0
              ? 'Your visits so far ran on your anonymous chat line — site teams do not have this number. '
              : ''}
            This ledger updates only if you ever confirm a visit with a reveal — one site team, one
            day, shown here.
          </p>
        </div>
        <div className="au-danger-row">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void signOut();
            }}
          >
            Sign out
          </button>
          <button
            type="button"
            className="au-del"
            disabled={busy}
            onClick={() => {
              if (!confirmDelete) {
                setConfirmDelete(true);
                return;
              }
              setBusy(true);
              void deleteProfile().then((res) => {
                setBusy(false);
                if (res.ok) setDeleted(true);
              });
            }}
          >
            {confirmDelete ? 'Tap again to erase' : 'Delete profile'}
          </button>
        </div>
        <p className="au-fine">
          Delete erases your key and synced board from Naya — immediately, permanently.
        </p>
      </div>
    </>
  );
}
